<template>
  <div
    class="quantity-selector"
    :class="{
      'quantity-selector--large': large,
      [$class('error')]: state === 'error',
      'has-graduated': graduatedText !== '',
    }"
  >
    <div
      v-if="!deposit"
      class="quantity-selector__control quantity-selector__control--button"
    >
      <button
        class="quantity-selector__button"
        :class="{ [$class('disabled')]: disableDecrease }"
        type="button"
        @click="handleQuantityUpdate('decrease')"
      >
        <span
          class="visually-hidden"
          v-text="$string('cart.line_item.decrease_quantity')"
        />

        <icon-minus />
      </button>
    </div>

    <div class="quantity-selector__control quantity-selector__control--input">
      <label
        class="visually-hidden"
        :for="`quantity-selector-input-${lineItem ? lineItem.key : 'product'}`"
        v-text="$string('cart.line_item.quantity')"
      />

      <input
        :id="`quantity-selector-input-${lineItem ? lineItem.key : 'product'}`"
        class="quantity-selector__input text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
        :name="name"
        pattern="[0-9]*"
        readonly
        tabindex="-1"
        type="number"
        :value="quantity"
        :disabled="unavailable"
      />
    </div>

    <div
      v-if="!deposit"
      class="quantity-selector__control quantity-selector__control--button"
    >
      <button
        class="quantity-selector__button"
        :class="{ [$class('disabled')]: disableIncrease }"
        type="button"
        @click="handleQuantityUpdate('increase')"
      >
        <span
          class="visually-hidden"
          v-text="$string('cart.line_item.increase_quantity')"
        />

        <icon-plus />
      </button>

      <div
        v-if="lineItem?.graduated_discount_products > 0"
        class="quantity-selector__graduated"
      >
        <span class="quantity-selector__graduated--icon">
          <icon-discount />
        </span>
        <p
          v-if="state !== 'updating' && state !== 'loading'"
          class="tooltip quantity-selector__graduated--text text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          :class="{
            success: maxTier,
          }"
          v-text="graduatedText"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Quantity selector (quantity-selector)
 * -----------------------------------------------------------------------------
 * Base template and functionality for quantity selector.
 * - Used in product form and line item.
 * - Responsible for emitting events to update quantity, parent components must
 *   action the update.
 *
 * @param {Boolean} [large] - Large styles.
 * @param {Object} [lineItem] - Line item object.
 * @param {Number} [min] - The minimum value the selector can be.
 * @param {Number} quantity - Actual line item quantity selector value as
 * reflected in Shopify's data.
 * @param {String} state - Parent component state.
 *
 * @emits updateQuantity - Emit new quantity value.
 *
 */
import IconMinus from '~icons/directional-navigation/minus.svg';
import IconPlus from '~icons/directional-navigation/plus.svg';
import IconDiscount from '~icons/general/discount.svg';

import { mapActions, mapState } from 'pinia';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'QuantitySelector',

  components: {
    IconMinus,
    IconPlus,
    IconDiscount,
  },

  props: {
    large: {
      type: Boolean,
      default: false,
    },
    lineItem: {
      type: [Boolean, Object],
      default: false,
    },
    min: {
      type: Number,
      default: 0,
    },
    quantity: {
      type: Number,
      default: 1,
      required: true,
    },
    state: {
      type: String,
      default: 'loading',
      required: true,
    },
    deposit: {
      type: Boolean,
      default: false,
    },
    itemDependenciesUpdating: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-quantity': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      localQuantity: 1,
      showGraduate: false,
      // checks final message for maximum graduated discount has been shown
      finalMessageShown: false,
    };
  },

  computed: {
    ...mapState(useCartStore, {
      getCartProductLimitReached: 'getCartProductLimitReached',
      cartItems: (store) => store.response.items,
    }),

    ...mapState(useDiscountCampaignsStore, {
      codeState: 'codeState',
      getGraduatedDiscountTiers: 'getGraduatedDiscountTiers',
    }),

    /**
     * Compute if decrease button should be disabled.
     * @return {Boolean}
     */
    disableDecrease() {
      return (
        this.unavailable ||
        this.isLoading ||
        this.localQuantity === this.min ||
        this.itemDependenciesUpdating
      );
    },

    /**
     * Compute if increase button should be disabled.
     * @return {Boolean}
     */
    disableIncrease() {
      const outOfInventory =
        this.lineItem?.inventory_management === 'shopify' &&
        this.lineItem?.inventory_policy === 'deny' &&
        this.quantity >= this.inventory;

      return (
        this.unavailable ||
        this.isLoading ||
        this.getCartProductLimitReached ||
        outOfInventory ||
        this.itemDependenciesUpdating
      );
    },

    /**
     * Compute inventory.
     * - Returns 0 if no limit.
     * @returns {Number}
     */
    inventory() {
      return this.lineItem?.inventory_quantity || 0;
    },

    /**
     * Compute input name.
     * @return {String}
     */
    name() {
      if (this.lineItem?.key) {
        return 'updates[]';
      }

      return 'quantity';
    },

    /**
     * Compute unavailable.
     * - Item is considered unavailable if available is false and has no quantity.
     * @returns {Boolean}
     */
    unavailable() {
      if (this.lineItem) {
        return this.lineItem.available === false && !this.lineItem.quantity;
      }

      return false;
    },

    graduatedDiscountTiers() {
      if (!this.lineItem || this.lineItem.graduated_discount_products === 0) {
        return [];
      }
      const tiers = this.getGraduatedDiscountTiers(
        this.lineItem.variant_id,
        this.lineItem.product_id,
        'active',
      )
        .map((tier) => ({
          percentage: tier.percentage,
          quantity: Number(tier.quantity),
        }))
        .sort((a, b) => a.quantity - b.quantity);

      return tiers;
    },

    nextTier() {
      if (!this.lineItem) {
        return null;
      }

      let nextTier = null;

      for (const tier of this.graduatedDiscountTiers) {
        if (
          !nextTier &&
          this.lineItem.graduated_discount_products < tier.quantity
        ) {
          nextTier = tier;
        }
      }

      return nextTier;
    },

    maxTier() {
      return (
        this.lineItem &&
        this.lineItem.graduated_discount_products > 0 &&
        !this.nextTier
      );
    },

    graduatedText() {
      if (!this.lineItem || this.lineItem.graduated_discount_products === 0) {
        return '';
      }

      if (this.maxTier) {
        return this.$string('discount_campaigns.graduated_discount.cart_max');
      }
      return this.$string(
        'discount_campaigns.graduated_discount.cart_missing',
        {
          replace: {
            percentage: this.nextTier?.percentage,
            amount:
              Number(this.nextTier?.quantity) -
              this.lineItem.graduated_discount_products,
          },
        },
      );
    },

    isLoading() {
      return ['loading', 'updating', 'adding'].includes(this.state);
    },
  },

  watch: {
    /**
     * Watch cart items to update graduated discount.
     * we need to change the graduatedtext whenever cartItems change
     * as different items have an effect on the graduated discount
     */
    cartItems() {
      if (this.lineItem && this.lineItem.graduated_discount_products > 0) {
        this.showGraduate = false;
      }
    },

    /**
     * Watch actual quantity to update local quantity
     * resets graduated discount
     */
    quantity() {
      this.localQuantity = this.quantity;
    },
  },

  mounted() {
    this.localQuantity = this.quantity || 1;
  },

  methods: {
    ...mapActions(useDiscountCampaignsStore, {
      setCodeState: 'setCodeState',
      setErrorType: 'setErrorType',
    }),
    /**
     * Handle quantity selector update.
     * - Buttons only update local quantity which emits an event.
     * - If the event is successful then the quantity prop is updated and this
     *   is used for front-end display.
     * @param {String} type - `decrease` or `increase`.
     */
    handleQuantityUpdate(type) {
      if (
        this.state === 'updating' ||
        (type === 'decrease' && this.disableDecrease) ||
        (type === 'increase' && this.disableIncrease)
      ) {
        return;
      }

      if (this.codeState === 'invalid') {
        this.setCodeState('ready');
        this.setErrorType('');
      }

      if (type === 'decrease') {
        this.localQuantity--;
      } else {
        this.localQuantity++;
      }

      this.$emit('update-quantity', this.localQuantity);
    },

    /**
     * Fade out tooltip after 3 sec and remove all graduated text.
     */
    fadeOutTooltip() {
      setTimeout(() => {
        this.showGraduate = false;
        this.graduatedText = '';
        this.finalMessageShown = true;
        this.inCartQuantity = 0;
      }, 1500);
    },
  },
};
</script>

<style lang="scss">
@import './quantity-selector';
</style>
